import { memo } from "react";
import { Box, Button, Theme } from "@mui/material";
import { Clear, Backspace } from "@mui/icons-material";

interface KeypadProps {
  onKeypadInput: (digit: string) => void;
  onClear?: () => void;
  onBackspace?: () => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const Keypad = memo(
  ({
    onKeypadInput,
    onClear,
    onBackspace,
    disabled = false,
    className,
    style,
  }: KeypadProps) => {
    const handleKeypadInput = (digit: string) => {
      if (!disabled) {
        onKeypadInput(digit);
      }
    };

    const handleClear = () => {
      if (!disabled && onClear) {
        onClear();
      }
    };

    const handleBackspace = () => {
      if (!disabled && onBackspace) {
        onBackspace();
      }
    };

    return (
      <Box
        className={className}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: (theme) => theme.palette.billetYard.backgrounds.gradient,
          border: (theme) =>
            `1px solid ${theme.palette.billetYard.borders.light}`,
          borderRadius: "12px",
          padding: "16px",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          ...style,
        }}
      >
        {/* Numeric Keyboard */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "450px", // Increased to accommodate bigger buttons
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "12px", // Increased from 8px for even better touch spacing
              width: "100%",
            }}
          >
            {/* First Row */}
            <Button
              onClick={() => handleKeypadInput("7")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              7
            </Button>
            <Button
              onClick={() => handleKeypadInput("8")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              8
            </Button>
            <Button
              onClick={() => handleKeypadInput("9")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              9
            </Button>

            {/* Second Row */}
            <Button
              onClick={() => handleKeypadInput("4")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              4
            </Button>
            <Button
              onClick={() => handleKeypadInput("5")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              5
            </Button>
            <Button
              onClick={() => handleKeypadInput("6")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              6
            </Button>

            {/* Third Row */}
            <Button
              onClick={() => handleKeypadInput("1")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              1
            </Button>
            <Button
              onClick={() => handleKeypadInput("2")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              2
            </Button>
            <Button
              onClick={() => handleKeypadInput("3")}
              sx={keyButtonSx}
              disabled={disabled}
            >
              3
            </Button>

            {/* Fourth Row */}
            {onClear && (
              <Button
                onClick={handleClear}
                sx={actionButtonSx}
                disabled={disabled}
              >
                <Clear />
              </Button>
            )}
            <Button
              onClick={() => handleKeypadInput("0")}
              sx={
                onClear ? keyButtonSx : { ...keyButtonSx, gridColumn: "span 2" }
              }
              disabled={disabled}
            >
              0
            </Button>
            {onBackspace && (
              <Button
                onClick={handleBackspace}
                sx={actionButtonSx}
                disabled={disabled}
              >
                <Backspace />
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

Keypad.displayName = "Keypad";

// Touch-optimized styles - EXTRA LARGE for industrial touch screens
const keyButtonSx = {
  backgroundColor: (theme: Theme) => theme.palette.grey[200],
  border: (theme: Theme) => `3px solid ${theme.palette.grey[300]}`,
  borderRadius: "10px",
  color: (theme: Theme) => theme.palette.grey[600],
  fontSize: "48px", // Increased from 36px - much bigger numbers
  fontWeight: "bold",
  minWidth: "0",
  height: "100px", // Increased from 80px - much bigger touch targets
  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: (theme: Theme) => theme.palette.billetYard.borders.medium,
    transform: "translateY(-2px)",
    boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
  },
};

const actionButtonSx = {
  backgroundColor: (theme: Theme) => theme.palette.grey[200],
  border: (theme: Theme) => `3px solid ${theme.palette.grey[300]}`,
  borderRadius: "10px",
  color: (theme: Theme) => theme.palette.grey[600],
  fontSize: "28px", // Increased from 24px
  fontWeight: "bold",
  minWidth: "0",
  height: "100px", // Increased from 80px - much bigger touch targets
  boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: (theme: Theme) => theme.palette.billetYard.borders.medium,
    transform: "translateY(-2px)",
    boxShadow: "0 6px 12px rgba(0,0,0,0.2)",
  },
};
